import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { Ambulance, Building2, Check, Phone, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDecideDispatch,
  useEmergencyDispatches,
  type EmergencyDispatch,
} from "@/shared/api/hooks/emergency";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { hasMinRole } from "@/shared/auth/permissions";
import { cn } from "@/shared/lib/cn";

/**
 * EmergencyDispatchCenter — 관제요원용 "119 발신 승인" 도크 (P4-A 통합).
 *
 * 심각 경보가 119 확인 게이트를 열면(`emergency.gate.opened`) 백엔드가 이 org 의
 * 경보 WS 로 카드 페이로드를 브로드캐스트한다. 여기서는 그 신호로 대기 목록을
 * 즉시 무효화하고, **실시간 카운트다운**(1초 감소)과 승인/반려 버튼을 렌더한다.
 * 승인 → 모의 119/CARIS 발신, 반려 → 미발신(이력만), 미승인 만료 → 서버 스윕
 * (`safety.tasks.expire_emergency_gates`, 30초 beat)이 `emergency.gate.decided`
 * (EXPIRED)로 통지하며 목록에서 사라진다.
 *
 * **레이아웃 — master-detail 도크.** 예전에는 우하단에 400×~330px 카드를 그대로
 * 쌓아 올려 화면 우측이 통째로 가려졌고(사용자 신고 "우측에 경고 팝업이 수도 없이
 * 쌓인다"), 그 다음 시도인 "1장 + 외 N건 모두 보기" 접기는 반대로 **목록이 안 보인다**는
 * 지적을 받았다("리스트가 보여야지"). 지금은 **상단 메뉴 바로 아래에 붙는 오른쪽
 * 레일**로:
 *
 *   * 위  — 선택된 1건의 승인 카드(상세 + 승인/반려),
 *   * 아래 — 대기 중 **전부**를 컴팩트한 한 줄(row)로, 접지 않고 스크롤로 열람,
 *   * 행을 누르면 위 상세가 그 건으로 바뀐다.
 *
 * 도크가 떠 있는 동안 `--dispatch-dock-w` 를 세팅해 AlarmCenter 가 그만큼 오른쪽을
 * 비워 준다(둘이 서로를 덮지 않게 하는 유일한 접점). z-85 = AlarmCenter(z-90) 아래 —
 * 좁은 화면에서 겹칠 때는 경보 오버레이가 이긴다(그 개별 확인/해제 버튼이 우선).
 *
 * 119 발신 승인·반려는 사이트관리자 이상만 가능(08 §3, D-R6). 사용자(operator)·
 * 작업자(worker) 에게는 이 승인 표면을 아예 띄우지 않는다.
 */

/** 도크 폭 — AlarmCenter 가 비워 두는 오른쪽 레일 폭과 같아야 한다. */
const DOCK_WIDTH = "380px";

export function EmergencyDispatchCenter() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;
  const isControlRole = hasMinRole(user?.role, "safety_manager");

  // 새 게이트/결정 → 대기 목록 즉시 무효화(폴백 폴링은 훅 내부).
  useOrgAlertChannel(orgId, (env) => {
    if (env.type === "emergency.gate.opened" || env.type === "emergency.gate.decided") {
      qc.invalidateQueries({ queryKey: qk.safety.emergency.all });
    }
  });

  const { data } = useEmergencyDispatches(undefined);
  const { data: sitesData } = useSites();
  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => s.id != null && m.set(s.id, s.name));
    return (id: number) => m.get(id) ?? t("widgets.emergency.siteFallback", { id });
  }, [sitesData, t]);

  // 관제요원이 로컬에서 닫은 카드(승인/반려 진행 중 애니메이션 등) 숨김 집합.
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  // 상세로 펼쳐 볼 건. null 이면 마감이 가장 임박한 건(목록 첫 줄).
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // 마감이 임박한 순서 — 가장 급한 건이 늘 목록 맨 위/기본 상세가 되도록.
  const pending = useMemo(
    () =>
      (data?.results ?? [])
        .filter((d) => d.state === "pending" && !dismissed.has(d.id))
        .sort(
          (a, b) =>
            +new Date(a.countdown_deadline) - +new Date(b.countdown_deadline),
        ),
    [data, dismissed],
  );

  const shown = isControlRole && pending.length > 0;

  // 도크가 차지하는 폭을 전역 토큰으로 알린다 → AlarmCenter 가 그만큼 비운다.
  // (렌더 중이 아니라 커밋 후에 쓴다. 언마운트 시 반드시 0 으로 되돌린다.)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--dispatch-dock-w", shown ? DOCK_WIDTH : "0px");
    return () => root.style.setProperty("--dispatch-dock-w", "0px");
  }, [shown]);

  if (!shown) return null;

  const selected = pending.find((d) => d.id === selectedId) ?? pending[0];
  const onDone = (id: number) => setDismissed((s) => new Set(s).add(id));

  return (
    <aside
      aria-label={t("widgets.emergency.dockTitle")}
      className={cn(
        // 상단 메뉴 바로 아래에 붙는 오른쪽 레일. 아래로는 화면 끝까지.
        "fixed right-0 top-[var(--header-h)] bottom-0 z-[85] flex flex-col",
        "w-[min(380px,100vw)] pointer-events-none",
      )}
    >
      <div
        className={cn(
          "pointer-events-auto flex max-h-full min-h-0 flex-col overflow-hidden",
          "border-l-2 border-b-2 border-t-[3px] rounded-bl-2xl",
        )}
        style={{
          borderColor: "var(--danger, #ef4444)",
          background: "var(--bg-elevated, var(--bg))",
          boxShadow: "var(--shadow-3, 0 12px 32px rgba(0,0,0,.28))",
          animation: "fade-in var(--motion-fast, 140ms) var(--ease-out-soft, ease) both",
        }}
      >
        {/* 도크 헤더 — 무엇이 몇 건 대기 중인지 */}
        <header
          className="flex shrink-0 items-center gap-2 px-3.5 py-2.5"
          style={{ background: "color-mix(in oklab, var(--danger,#ef4444) 14%, transparent)" }}
        >
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white"
            style={{ background: "var(--danger, #ef4444)" }}
          >
            <Ambulance size={16} strokeWidth={2.1} />
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px] font-bold">
            {t("widgets.emergency.dockTitle")}
          </span>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[12px] font-semibold tabular-nums text-white"
            style={{ background: "var(--danger, #ef4444)" }}
          >
            {t("widgets.emergency.waitingCount", { n: pending.length })}
          </span>
        </header>

        {/* 위 — 선택된 1건의 상세 + 승인/반려 */}
        <div className="max-h-[55%] shrink-0 overflow-y-auto overscroll-contain">
          <DispatchCard
            key={selected.id}
            dispatch={selected}
            siteName={siteName(selected.site)}
            onDone={() => onDone(selected.id)}
          />
        </div>

        {/* 아래 — 대기 전부를 컴팩트한 행으로. 접지 않는다, 넘치면 스크롤. */}
        <div className="flex min-h-0 flex-1 flex-col border-t border-[var(--separator)]">
          <div className="shrink-0 px-3.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-subtle)]">
            {t("widgets.emergency.listTitle")}
          </div>
          <ul
            aria-label={t("widgets.emergency.listTitle")}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2"
          >
            {pending.map((d) => (
              <DispatchRow
                key={d.id}
                dispatch={d}
                siteName={siteName(d.site)}
                selected={d.id === selected.id}
                onSelect={() => setSelectedId(d.id)}
              />
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

/** 대기 목록 한 줄 — 카운트다운 + 현장·가스 + RI. 누르면 위 상세가 바뀐다. */
function DispatchRow({
  dispatch,
  siteName,
  selected,
  onSelect,
}: {
  dispatch: EmergencyDispatch;
  siteName: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const remaining = useCountdown(dispatch.countdown_deadline);
  const urgent = remaining <= 30;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected}
        className={cn(
          "flex w-full items-center gap-2.5 px-3.5 py-2 text-left transition-colors",
          "border-l-[3px] hover:bg-[var(--bg-hover)]",
          selected
            ? "border-l-[var(--danger,#ef4444)] bg-[var(--bg-subtle)]"
            : "border-l-transparent",
        )}
      >
        <span
          className="w-[46px] shrink-0 rounded-md px-1 py-0.5 text-center text-[12.5px] font-bold tabular-nums"
          style={{
            color: urgent ? "var(--danger,#ef4444)" : "var(--fg-muted)",
            background: urgent
              ? "color-mix(in oklab, var(--danger,#ef4444) 16%, transparent)"
              : "var(--bg-subtle)",
          }}
        >
          {mmss(remaining)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight">
            {siteName}
          </span>
          <span className="block truncate text-[11.5px] leading-tight text-[var(--fg-subtle)]">
            {gasLabel(t, dispatch.gas)} · {t("widgets.emergency.ri")}{" "}
            {dispatch.ri_at_open.toFixed(2)}
          </span>
        </span>
      </button>
    </li>
  );
}

function DispatchCard({
  dispatch,
  siteName,
  onDone,
}: {
  dispatch: EmergencyDispatch;
  siteName: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const decide = useDecideDispatch();
  const remaining = useCountdown(dispatch.countdown_deadline);
  const [confirmReject, setConfirmReject] = useState(false);

  const call = dispatch.payload?.call_119 ?? {};
  const expired = remaining <= 0;

  const approve = () =>
    decide.mutate(
      { id: dispatch.id, decision: "approve" },
      { onSuccess: onDone },
    );
  const reject = () =>
    decide.mutate(
      { id: dispatch.id, decision: "reject" },
      { onSuccess: onDone },
    );

  return (
    <section
      role="alertdialog"
      aria-label={t("widgets.emergency.cardAria", { site: siteName })}
      // 도크 안에 끼워지는 상세 — 테두리/그림자는 도크가 갖는다(이중 액자 방지).
      className="text-[var(--fg)]"
      style={{
        animation: "fade-in var(--motion-fast, 140ms) var(--ease-out-soft, ease) both",
      }}
    >
      {/* header band */}
      <header className="flex items-center gap-2.5 border-b border-[var(--separator)] px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-subtle)]">
            {t("widgets.emergency.requestTitle")}
          </div>
          <div className="truncate text-[15px] font-bold leading-tight">
            {siteName} · {gasLabel(t, dispatch.gas)}
          </div>
        </div>
        <Countdown seconds={remaining} />
      </header>

      {/* body */}
      <div className="px-4 py-3.5">
        <div className="flex items-center justify-between gap-3 text-[13px]">
          <span className="text-[var(--fg-subtle)]">{t("widgets.emergency.ri")}</span>
          <span className="tabular-nums font-semibold" style={{ color: "var(--danger,#ef4444)" }}>
            {dispatch.ri_at_open.toFixed(2)}
          </span>
        </div>
        {call.concentration && (
          <div className="mt-1 flex items-center justify-between gap-3 text-[13px]">
            <span className="text-[var(--fg-subtle)]">{t("widgets.emergency.concentration")}</span>
            <span className="tabular-nums font-medium">{call.concentration}</span>
          </div>
        )}

        {call.tts_script && (
          <p className="mt-3 rounded-xl bg-[var(--bg-subtle)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--fg-muted)]">
            <Phone size={12} className="mr-1 inline align-[-1px]" />
            {call.tts_script}
          </p>
        )}
        {call.blueprint_url && (
          <a
            href={call.blueprint_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-[var(--accent)] hover:underline"
          >
            <Building2 size={12} /> {t("widgets.emergency.viewBlueprint")}
          </a>
        )}

        {decide.isError && (
          <p className="mt-2.5 text-[12px] text-[var(--danger,#ef4444)]">
            {t("widgets.emergency.decideFailed")}
          </p>
        )}
        {expired && (
          <p className="mt-2.5 text-[12px] text-[var(--fg-subtle)]">
            {t("widgets.emergency.expired")}
          </p>
        )}

        {/* actions */}
        <div className="mt-3.5 grid grid-cols-2 gap-2.5">
          {confirmReject ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmReject(false)}
                disabled={decide.isPending}
                className="flex h-11 items-center justify-center rounded-xl border border-[var(--border)] text-[14px] font-semibold transition-colors hover:bg-[var(--bg-hover)]"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={reject}
                disabled={decide.isPending}
                className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-[var(--border)] text-[14px] font-semibold text-[var(--fg-muted)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-60"
              >
                <X size={16} strokeWidth={2.25} /> {t("widgets.emergency.rejectConfirm")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setConfirmReject(true)}
                disabled={decide.isPending}
                className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-[var(--border)] text-[14px] font-semibold text-[var(--fg-muted)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-60"
              >
                <X size={16} strokeWidth={2.25} /> {t("widgets.emergency.reject")}
              </button>
              <button
                type="button"
                onClick={approve}
                disabled={decide.isPending || expired}
                className={cn(
                  "flex h-11 items-center justify-center gap-1.5 rounded-xl text-[14px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60",
                )}
                style={{ background: "var(--danger, #ef4444)" }}
              >
                <Check size={16} strokeWidth={2.5} />
                {decide.isPending ? t("widgets.emergency.dispatching") : t("widgets.emergency.approve")}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/** 남은 초 → `m:ss` (음수는 0:00). 카드 배지와 목록 행이 같은 포맷을 쓴다. */
function mmss(seconds: number): string {
  const s = Math.max(0, seconds);
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

/** mm:ss 카운트다운 배지 — 30초 이하는 붉게 강조. */
function Countdown({ seconds }: { seconds: number }) {
  const urgent = Math.max(0, seconds) <= 30;
  return (
    <span
      className="shrink-0 rounded-lg px-2.5 py-1 text-[15px] font-bold tabular-nums"
      style={{
        color: urgent ? "var(--danger,#ef4444)" : "var(--fg)",
        background: urgent
          ? "color-mix(in oklab, var(--danger,#ef4444) 16%, transparent)"
          : "var(--bg-subtle)",
      }}
    >
      {mmss(seconds)}
    </span>
  );
}

/** 남은 초를 1초마다 실시간 감소 (다동 매니져 DispatcherTimer 패턴). */
function useCountdown(deadlineIso: string): number {
  const deadline = useMemo(() => new Date(deadlineIso).getTime(), [deadlineIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return Math.round((deadline - now) / 1000);
}

/** 현지화된 가스명을 가진 코드 — 라벨은 렌더 시점에 t() 로 조회한다. */
const GAS_CODES = ["CO", "H2S", "NH3", "CH4", "CO2", "O2", "SO2", "NO2", "VOC"];
function gasLabel(t: TFunction, code: string): string {
  if (!code) return t("widgets.emergency.gas.composite");
  const c = code.toUpperCase();
  return GAS_CODES.includes(c) ? t(`widgets.emergency.gas.${c}`) : code;
}
